import { FC, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Formik, Form, Field, FieldProps } from 'formik';
import { AiOutlineEye, AiOutlineEyeInvisible, AiOutlineMail, AiOutlineLock } from 'react-icons/ai';
import { AuthLayout } from '../../../layouts';
import { DAV_ROLES, loginSchema } from '../../../../constants';
import { DAV_APIS } from '../../../../adapters';
import { authenticationSuccess } from '../../../../store/slices/authSlice';
import { SaveTokenInLocalStorage } from '../../../../store/actions';
import { Alert } from '../../../commons';

interface LoginProps {}

const Login: FC<LoginProps> = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const userRouteRedirect = (role: string) => {
    switch (role) {
      case DAV_ROLES.AGENT:
        navigate('/dashboard/countries');
        break;
      case DAV_ROLES.SUPER_ADMIN:
        navigate('/dashboard/overview');
        break;
      default:
        navigate('/dashboard/tours');
    }
  };

  const handleLogin = async (values: any) => {
    setLoading(true);
    try {
      const data = {
        email: values.email,
        password: values.password,
      };
      const res = await DAV_APIS.auth.login(data);
      if (res.status === 200) {
        dispatch(authenticationSuccess(res.data));
        SaveTokenInLocalStorage(dispatch, res.data);
        userRouteRedirect(res.data.user.role);
      }
      setLoading(false);
      Alert({ message: 'Login successful', variant: 'success' });
    } catch (error) {
      setLoading(false);
      Alert({ message: 'Login failed!', variant: 'error' });
    }
  };

  return (
    <AuthLayout title='Welcome Back' subtitle='Sign in to your account to continue'>
      <Formik
        enableReinitialize
        initialValues={{
          email: '',
          password: '',
        }}
        validationSchema={loginSchema}
        onSubmit={async (values, { resetForm }) => {
          await handleLogin(values);
          resetForm();
        }}
      >
        {() => (
          <Form className='space-y-5'>
            {/* Email Field */}
            <Field name='email'>
              {({ field, meta }: FieldProps) => (
                <div className='space-y-2'>
                  <label 
                    htmlFor='email' 
                    className='block text-sm font-medium text-gray-700 dark:text-gray-300'
                  >
                    Email Address
                  </label>
                  <div className='relative'>
                    <div className='absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none'>
                      <AiOutlineMail className='h-5 w-5 text-gray-400' />
                    </div>
                    <input
                      {...field}
                      id='email'
                      type='email'
                      autoComplete='email'
                      placeholder='you@example.com'
                      className={`
                        w-full h-12 pl-12 pr-4 
                        bg-gray-50 dark:bg-gray-800 
                        border rounded-xl
                        text-gray-900 dark:text-white
                        placeholder:text-gray-400
                        transition-all duration-200
                        focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500
                        ${meta.touched && meta.error ? 
                          'border-red-500 focus:ring-red-500/20 focus:border-red-500' 
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                        }
                      `}
                    />
                  </div>
                  {meta.touched && meta.error && (
                    <p className='text-sm text-red-500 flex items-center gap-1'>
                      <span className='inline-block w-1 h-1 rounded-full bg-red-500' />
                      {meta.error}
                    </p>
                  )}
                </div>
              )}
            </Field>

            {/* Password Field */}
            <Field name='password'>
              {({ field, meta }: FieldProps) => (
                <div className='space-y-2'>
                  <div className='flex items-center justify-between'>
                    <label 
                      htmlFor='password' 
                      className='block text-sm font-medium text-gray-700 dark:text-gray-300'
                    >
                      Password
                    </label>
                    <Link 
                      to='/forgot-password'
                      className='text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors'
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div className='relative'>
                    <div className='absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none'>
                      <AiOutlineLock className='h-5 w-5 text-gray-400' />
                    </div>
                    <input
                      {...field}
                      id='password'
                      type={showPassword ? 'text' : 'password'}
                      autoComplete='current-password'
                      placeholder='••••••••'
                      className={`
                        w-full h-12 pl-12 pr-12 
                        bg-gray-50 dark:bg-gray-800 
                        border rounded-xl
                        text-gray-900 dark:text-white
                        placeholder:text-gray-400
                        transition-all duration-200
                        focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500
                        ${meta.touched && meta.error ? 
                          'border-red-500 focus:ring-red-500/20 focus:border-red-500' 
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                        }
                      `}
                    />
                    <button
                      type='button'
                      onClick={() => setShowPassword(!showPassword)}
                      className='absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors'
                    >
                      {showPassword ? (
                        <AiOutlineEyeInvisible className='h-5 w-5' />
                      ) : (
                        <AiOutlineEye className='h-5 w-5' />
                      )}
                    </button>
                  </div>
                  {meta.touched && meta.error && (
                    <p className='text-sm text-red-500 flex items-center gap-1'>
                      <span className='inline-block w-1 h-1 rounded-full bg-red-500' />
                      {meta.error}
                    </p>
                  )}
                </div>
              )}
            </Field>

            {/* Submit Button */}
            <button
              type='submit'
              disabled={loading}
              style={{
                background: 'linear-gradient(to right, #10B981, #059669)',
              }}
              className='
                w-full h-12 
                text-white font-semibold
                rounded-xl
                shadow-lg
                transition-all duration-200
                hover:opacity-90
                hover:-translate-y-0.5
                active:translate-y-0
                disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0
                flex items-center justify-center gap-2
              '
            >
              {loading ? (
                <>
                  <svg 
                    className='animate-spin h-5 w-5' 
                    viewBox='0 0 24 24'
                    fill='none'
                  >
                    <circle 
                      className='opacity-25' 
                      cx='12' 
                      cy='12' 
                      r='10' 
                      stroke='currentColor' 
                      strokeWidth='4'
                    />
                    <path 
                      className='opacity-75' 
                      fill='currentColor' 
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    />
                  </svg>
                  <span>Signing in...</span>
                </>
              ) : (
                'Sign In'
              )}
            </button>

            {/* Divider */}
            <div className='relative my-6'>
              <div className='absolute inset-0 flex items-center'>
                <div className='w-full border-t border-gray-200 dark:border-gray-700' />
              </div>
              <div className='relative flex justify-center'>
                <span className='px-4 bg-white dark:bg-gray-900 text-sm text-gray-500'>
                  New to Dav Safaris?
                </span>
              </div>
            </div>

            {/* Register Link */}
            <Link
              to='/register'
              className='
                w-full h-12 
                flex items-center justify-center
                border-2 border-primary-500
                text-primary-600 font-semibold
                rounded-xl
                transition-all duration-200
                hover:bg-primary-50 dark:hover:bg-primary-500/10
              '
            >
              Create an Account
            </Link>
          </Form>
        )}
      </Formik>
    </AuthLayout>
  );
};

export default Login;
